import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {forkJoin, Observable} from 'rxjs';
import {UserService} from '@core/services/auth/user/user.service';
import {
  MarketLevelOrganizationService
} from '@core/services/administration/market-level-organization/market-level-organization.service';
import {PointOfSaleService} from '@core/services/administration/point-of-sale/point-of-sale.service';
import {
  CompanyLevelOrganizationService
} from '@core/services/administration/company-level-organization/company-level-organization.service';
import {CompanyService} from '@core/services/administration/company/company.service';
import {HabilitationService} from '@core/auth/habilitation/habilitation.service';
import {ProfileService} from '@core/services/auth/profile/profile.service';
import {ContributorService} from '@core/services/administration/contributor/contributor.service';
import {SubContributorService} from "@core/services/administration/sub-contributor/sub-contributor.service";

@Injectable({
  providedIn: 'root'
})
export class AdministrationResolver implements Resolve<any> {

  private _managementEntity: string = '';

  /**
   * Constructor
   */
  constructor(
    private _habilitationService: HabilitationService,
    private _userService: UserService,
    private _marketLevelOrganizationService: MarketLevelOrganizationService,
    private _pointOfSaleService: PointOfSaleService,
    private _companyLevelOrganizationService: CompanyLevelOrganizationService,
    private _companyService: CompanyService,
    private _profileService: ProfileService,
    private _contributorService: ContributorService,
    private _subContributorService: SubContributorService
  ) {
    this._userService.user$.subscribe((user) => {
      if (user) {
        this._managementEntity = user.managementEntity;
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Use this resolver to resolve initial mock-api for the application
   *
   * @param route
   * @param state
   */
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    // Fork join multiple API endpoint calls to wait all of them to finish

    let resolList: Observable<any>[] = [];
    if (this._habilitationService.has('VIEW_USERS')) {
      resolList.push(this._userService.getUsersByManagementEntity(
        this._managementEntity
      ));
    }
    if (this._habilitationService.has('VIEW_NETWORK')) {
      resolList.push(this._marketLevelOrganizationService.getAll());
      resolList.push(this._marketLevelOrganizationService.getLinked());
      resolList.push(this._pointOfSaleService.getAll());
      resolList.push(this._pointOfSaleService.getLinkedBrokers());
    }
    if (this._habilitationService.has('MANAGE_USERS')) {
      resolList.push(this._profileService.getAll());
    }
    // Le catalogue d'habilitations grantables est désormais T2 (scopé par `level`) :
    // chargé paresseusement dans l'éditeur de profils dès que l'admin choisit le niveau.
    // → plus de préchargement global ici.
    if (this._habilitationService.has('VIEW_NETWORK')) {
      resolList.push(this._pointOfSaleService.getBrokers());
      resolList.push(this._companyLevelOrganizationService.getAll());
      resolList.push(this._companyService.getAll());
      resolList.push(this._companyService.getLinked());
    }

    if (this._habilitationService.has('VIEW_NETWORK')) {
      resolList.push(this._contributorService.getAll());
      resolList.push(this._contributorService.getAllCanHaveSubContributors());
      resolList.push(this._contributorService.getContributorTypes());

      resolList.push(this._subContributorService.getAll());
      resolList.push(this._subContributorService.getSubContributorTypes());
    }

    return forkJoin(resolList)
  }
}
